import { useEffect, useState } from "react";
import { api, money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { KeyRound, Receipt } from "lucide-react";

export default function Shifts() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinSet, setPinSet] = useState(false);

  useEffect(() => {
    api.get("/pos/shifts").then(({ data }) => setShifts(data)).catch(() => {});
    api.get("/settings/manager-pin").then(({ data }) => setPinSet(data.configured)).catch(() => {});
  }, []);

  const savePin = async () => {
    try { await api.put("/settings/manager-pin", { pin }); toast.success("Manager PIN updated"); setPinOpen(false); setPin(""); setPinSet(true); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const open = async (s) => { const { data } = await api.get(`/pos/shifts/${s.id}`); setDetail(data); };
  const fmtTime = (v) => v ? new Date(v).toLocaleString("en-ZA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div>
      <PageHeader title="POS Shifts & Z-Reports" subtitle="End-of-day cash-ups with counted vs expected variance per cashier">
        {user?.role === "super_admin" && <Button variant="outline" onClick={() => setPinOpen(true)} data-testid="set-manager-pin-button"><KeyRound className="h-4 w-4 mr-1.5" /> {pinSet ? "Change" : "Set"} Manager PIN</Button>}
      </PageHeader>
      {!pinSet && <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-2" data-testid="pin-warning">No manager PIN configured — POS discounts are blocked until a super admin sets one.</div>}
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Z No.</TableHead><TableHead>Cashier</TableHead><TableHead>Opened</TableHead><TableHead>Closed</TableHead><TableHead className="text-right">Sales</TableHead><TableHead className="text-right">Txns</TableHead><TableHead className="text-right">Expected cash</TableHead><TableHead className="text-right">Counted</TableHead><TableHead className="text-right">Variance</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {shifts.map((s) => (
              <TableRow key={s.id} data-testid={`shift-row-${s.id}`} className="cursor-pointer" onClick={() => open(s)}>
                <TableCell className="font-mono text-sm">{s.z_number || (s.status === "open" ? <Badge className="bg-green-600">Open</Badge> : "—")}</TableCell>
                <TableCell>{s.operator_name}</TableCell>
                <TableCell className="text-sm">{fmtTime(s.opened_at)}</TableCell>
                <TableCell className="text-sm">{fmtTime(s.closed_at)}</TableCell>
                <TableCell className="text-right tabular">{s.summary ? money(s.summary.total) : "—"}</TableCell>
                <TableCell className="text-right tabular">{s.summary?.count ?? "—"}</TableCell>
                <TableCell className="text-right tabular">{s.expected_cash != null ? money(s.expected_cash) : "—"}</TableCell>
                <TableCell className="text-right tabular">{s.counted_cash != null ? money(s.counted_cash) : "—"}</TableCell>
                <TableCell className={`text-right tabular font-bold ${s.variance < 0 ? "text-primary" : s.variance > 0 ? "text-green-700" : ""}`}>{s.variance != null ? (s.variance > 0 ? "+" : "") + money(s.variance).replace("R ", "R ") : "—"}</TableCell>
                <TableCell><Receipt className="h-4 w-4 text-muted-foreground" /></TableCell>
              </TableRow>
            ))}
            {shifts.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No shifts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-display text-xl">Manager PIN</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Cashiers must enter this PIN to apply a discount at the till.</p>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">New PIN (4–8 digits)</Label><Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="manager-pin-input" /></div>
          <DialogFooter><Button onClick={savePin} className="w-full bg-primary hover:bg-[#C81219] text-white" data-testid="save-manager-pin-button">Save PIN</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Z-Report {detail?.z_number || ""} — {detail?.operator_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-2">
              <L l="Opening float" v={money(detail.opening_float)} />
              <L l="Cash sales" v={money(detail.summary?.cash)} /><L l="Card sales" v={money(detail.summary?.card)} /><L l="EFT sales" v={money(detail.summary?.eft)} />
              <L l="Discounts given" v={money(detail.summary?.discounts)} /><L l="Cash refunds" v={money(detail.cash_refunds || 0)} />
              <L l="Transactions / voided" v={`${detail.summary?.count ?? 0} / ${detail.summary?.voided ?? 0}`} />
              <div className="border-t border-border pt-2 flex justify-between font-bold"><span>Total sales</span><span className="tabular text-primary">{money(detail.summary?.total)}</span></div>
              {detail.status === "closed" && <>
                <L l="Expected cash in drawer" v={money(detail.expected_cash)} /><L l="Counted cash" v={money(detail.counted_cash)} />
                <div className={`flex justify-between font-bold ${detail.variance < 0 ? "text-primary" : detail.variance > 0 ? "text-green-700" : ""}`}><span>Variance</span><span className="tabular" data-testid="z-variance">{detail.variance > 0 ? "+" : ""}{money(detail.variance)}</span></div>
                {detail.notes && <p className="text-muted-foreground">Note: {detail.notes}</p>}
              </>}
              <div className="text-xs uppercase tracking-wide text-muted-foreground pt-2">Transactions</div>
              <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                {detail.transactions.map((t) => <div key={t.id} className={`flex justify-between px-3 py-1.5 ${t.voided ? "line-through text-muted-foreground" : ""}`}><span className="font-mono text-xs">{t.number} <span className="capitalize">· {t.payment_method}</span></span><span className="tabular">{money(t.total)}</span></div>)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function L({ l, v }) { return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="tabular">{v}</span></div>; }
